import { AnalyticsService } from '../../services/analytics-service';
import { Segment } from '../../services/analytics-services/segment';
import { INSURANCE_FIELD_VALUE_UPDATED } from '../../constants/analytics-events';
import { InsuranceProviders, getInsuranceProviderByName } from '../../enums/insurance-providers';
import { SellerSettings } from '../../enums/seller-settings/seller-settings';
import { EventSourceStrings } from '../../tracking/tracking-constants';
import * as AppActions from '../../store/app/app.actions';
import { CSW_CREATE_LABEL, CSW_CHANGE_INSURED_VALUE } from '../csw/csw.actions';
import Global from '../../store/global-selectors';
import { SellerProperties } from '../seller/seller-properties';

const identifyUser = (state) => {
  const sellerProperties = new SellerProperties({ state });
  const isNativeV3Seller = sellerProperties.isSellerSettingEnabled('IsNativeV3Seller');
  const userInfo = {
    ...Global.selectUserCurrentUserInfo(state),
    currentUIVersion: Global.selectUserUserSetting(state, {
      userSetting: 'CurrentUIVersion',
    }),
    isNativeV3Seller,
    plan: sellerProperties.subscriptionPlanInfo.planCode,
    isAccountOwner: Global.selectUserIsAccountOwner(state),
  };

  const sellerInfo = {
    homeCountry: sellerProperties.homeCountry,
    minimumVersion: sellerProperties.getSellerSetting('ShipStationUI.MinimumSemVersion'),
    planName: sellerProperties.subscriptionPlanInfo.planName,
    earlyAccessEnabled: sellerProperties.earlyAccessEnabled,
    labelProviders: sellerProperties.labelProviders,
    companyName: sellerProperties.company,
    isNativeV3Seller,
    shipmentLimit: Global.selectSellerPlanInfo(state)?.plan?.shipmentLimit,
    isInTrial: sellerProperties.isInTrial,
    trialEndDate: sellerProperties.trialEndDate,
    daysUntilTrialEnd: sellerProperties.daysUntilTrialEnd,
  };

  const { userId } = userInfo;
  const { sellerId } = sellerProperties;
  AnalyticsService.identifyUser(userId, sellerId, userInfo, sellerInfo);
};

export const getTrackingMetadata = (state) => {
  const { sellerId } = new SellerProperties({ state });

  return {
    sellerId,
  };
};

const insuranceAutomationSettingMap = {
  [InsuranceProviders.XCover.insuranceProviderId]: SellerSettings.insuranceSystemAutomationApplyXCover.key,
  [InsuranceProviders.ParcelGuard.insuranceProviderId]: SellerSettings.insuranceSystemAutomationApplyParcelGuard.key,
};

const getInsuranceAnalyticsData = (state, fulfillmentPlanId) => {
  const insuranceProviderName = Global.selectEntitiesFulfillmentPlanProperty(state, {
    fulfillmentPlanId,
    property: 'insuranceProvider',
  });
  const packages =
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'labelConfiguration.packages',
    }) ?? [];
  const totalInsuredValue = packages.reduce((sum, pkg) => sum + (pkg?.insuredValue?.value ?? 0), 0);
  const provider = getInsuranceProviderByName(insuranceProviderName);
  const autoInsureSettingKey = provider ? insuranceAutomationSettingMap[provider.insuranceProviderId] : null;
  const isAutoInsured = autoInsureSettingKey
    ? Global.selectSellerSellerSetting(state, { settingName: autoInsureSettingKey }) === 'true'
    : false;

  return {
    insuranceProvider: insuranceProviderName,
    totalInsuredValue,
    isAutoInsured,
    sellerId: Global.selectSellerSellerId(state),
    planName: Global.selectSellerPlanInfo(state)?.plan?.name,
  };
};

const actionTypeToHandlerMap = {
  [AppActions.APP_RENDER_SUCCESS]: (state) => {
    identifyUser(state);
  },
};

const eventBlacklist = [];

export const AnalyticsMiddleware = ({ getState }) => {
  return (next) => {
    return (action) => {
      const matchingHandler = actionTypeToHandlerMap[action.type];
      const state = getState();

      if (matchingHandler) {
        matchingHandler(state, action);
        return next(action);
      }
      if (eventBlacklist.indexOf(action.type) !== -1) {
        return next(action);
      }

      if (action.type === CSW_CREATE_LABEL) {
        const insuranceData = getInsuranceAnalyticsData(state, action.fulfillmentPlanId);
        const enrichedAction = { ...action, ...insuranceData };
        AnalyticsService.trackEvent(action.type, enrichedAction, getTrackingMetadata(state));
        return next(action);
      }

      if (action.type === CSW_CHANGE_INSURED_VALUE) {
        const result = next(action);
        const updatedState = getState();
        const insuranceData = getInsuranceAnalyticsData(updatedState, action.fulfillmentPlanId);
        Segment.trackEvent(INSURANCE_FIELD_VALUE_UPDATED, {
          ...insuranceData,
          insuredValue: insuranceData.totalInsuredValue,
          source: EventSourceStrings.CSW_INSURANCE_FIELD,
        });
        AnalyticsService.trackEvent(action.type, action, getTrackingMetadata(updatedState));
        return result;
      }

      AnalyticsService.trackEvent(action.type, action, getTrackingMetadata(state));
      return next(action);
    };
  };
};
